import type { AppContentLocale, PublishedAnnouncement } from "@yahshi/contracts";
import { pickLocaleCopy, publishedAnnouncementSchema, publishedAnnouncementListSchema } from "@yahshi/contracts";
import { Alert, Button, Result, Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useOptionalAnnouncementApi } from "../store/AnnouncementStore";
import { useAdminRuntime } from "../../../app/AdminRuntime";
import { resolveUserCenterApiBase } from "../../m1-app/data/appIntegration";
import { publicAnnouncementPath } from "../data/publicDocument";
import { PublicLocaleSwitch } from "../../../shared/i18n/PublicLocaleSwitch";
import "../content.css";

async function fetchPublishedList(appId: string): Promise<PublishedAnnouncement[]> {
  const response = await fetch(`${resolveUserCenterApiBase()}/open/apps/${encodeURIComponent(appId)}/announcements`, { headers: { accept: "application/json" } });
  const payload = await response.json() as { success?: boolean; data?: { items?: PublishedAnnouncement[] }; error?: { message?: string } };
  if (!response.ok || !payload.success || !payload.data) throw new Error(payload.error?.message ?? "无法读取公告");
  return publishedAnnouncementListSchema.parse(payload.data).items;
}

async function fetchPublished(appId: string, id: string): Promise<PublishedAnnouncement> {
  const response = await fetch(`${resolveUserCenterApiBase()}/open/apps/${encodeURIComponent(appId)}/announcements/${encodeURIComponent(id)}`, { headers: { accept: "application/json" } });
  const payload = await response.json() as { success?: boolean; data?: PublishedAnnouncement; error?: { message?: string } };
  if (!response.ok || !payload.success || !payload.data) throw new Error(payload.error?.message ?? "公告不存在或未在生效窗口");
  return publishedAnnouncementSchema.parse(payload.data);
}

export function PublicAnnouncementPage() {
  const { appId = "", id } = useParams();
  const [params, setParams] = useSearchParams();
  const locale = useMemo<AppContentLocale>(() => params.get("lang") === "ug" ? "ug" : "zh", [params]);
  const search = locale === "ug" ? "?lang=ug" : "";
  const runtime = useAdminRuntime();
  const api = useOptionalAnnouncementApi();
  const listQuery = useQuery({
    queryKey: ["public-announcements", appId, runtime.mode],
    enabled: Boolean(appId) && !id,
    retry: false,
    staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: "always", refetchInterval: 30_000,
    queryFn: async () => {
      const items = publishedAnnouncementListSchema.parse({items: await (api ? api.listPublished(appId) : fetchPublishedList(appId))}).items;
      if(items.some(x=>x.appId!==appId)) throw new Error("公告所属应用不匹配"); return items;
    },
  });
  const detailQuery = useQuery({
    queryKey: ["public-announcement", appId, id, runtime.mode],
    enabled: Boolean(appId && id),
    retry: false,
    staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: "always", refetchInterval: 30_000,
    queryFn: async () => {
      const item = publishedAnnouncementSchema.parse(await (api && id ? api.getPublished(appId,id) : fetchPublished(appId,id??"")));
      if(item.appId!==appId || item.id!==id) throw new Error("公告所属应用或编号不匹配"); return item;
    },
  });
  const switchLocale = (next: AppContentLocale) => {const updated=new URLSearchParams(params);updated.set("lang",next);setParams(updated,{replace:true});};
  if (!appId) return <Result status="warning" title="缺少应用" />;
  if (id) {
    if (detailQuery.isPending) return <main className="public-document" aria-busy="true"><Spin size="large" /></main>;
    if (detailQuery.error) return <main className="public-document"><Alert type="error" showIcon title="无法打开公告" description={detailQuery.error.message} action={<Button onClick={()=>void detailQuery.refetch()}>重试</Button>} /></main>;
    const item = detailQuery.data!;
    const copy = pickLocaleCopy(item, locale);
    return <main className="public-document" aria-labelledby="public-document-title" lang={locale === "ug" ? "ug" : "zh"} dir={locale === "ug" ? "rtl" : "ltr"}>
      <PublicLocaleSwitch locale={locale} onChange={switchLocale} />
      <Button loading={detailQuery.isFetching} onClick={()=>void detailQuery.refetch()}>刷新公告</Button>
      <p className="public-document-kicker">应用公告 · v{item.version}</p>
      <h1 id="public-document-title">{copy.title}</h1>
      <p className="public-document-meta">发布于 {new Date(item.publishedAt).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false })}（北京时间）</p>
      <pre className="public-document-body">{copy.body}</pre>
    </main>;
  }
  if (listQuery.isPending) return <main className="public-document" aria-busy="true"><Spin size="large" /></main>;
  if (listQuery.error) return <main className="public-document"><Alert type="error" showIcon title="无法打开公告" description={listQuery.error.message} action={<Button onClick={()=>void listQuery.refetch()}>重试</Button>} /></main>;
  const items = listQuery.data ?? [];
  return <main className="public-document" aria-labelledby="public-document-title" lang={locale === "ug" ? "ug" : "zh"} dir={locale === "ug" ? "rtl" : "ltr"}>
    <PublicLocaleSwitch locale={locale} onChange={switchLocale} />
    <Button loading={listQuery.isFetching} onClick={()=>void listQuery.refetch()}>刷新公告</Button>
    <p className="public-document-kicker">应用公告</p>
    <h1 id="public-document-title">当前生效公告</h1>
    <p className="public-document-meta">只展示已发布且在生效窗口内的纯文本公告</p>
    {items.length === 0 ? <p className="content-consent-note">当前没有生效公告</p> : (
      <ul className="public-announcement-list">
        {items.map((item) => (
          <li key={item.id}>
            <Link to={`${publicAnnouncementPath(item.appId, item.id)}${search}`}>{pickLocaleCopy(item, locale).title}</Link>
            <small>v{item.version}</small>
          </li>
        ))}
      </ul>
    )}
  </main>;
}
export default PublicAnnouncementPage;
